import * as React from "react"

const MILLISECS_BEFORE_SWITCH = 50

/**
 * This hook prevents immediately rendering new items, when items change rapidly (ie up/down arrow key pressed).
 *
 * ## How to use?
 * Make the component using the hook memoise its rendering by item.
 *
 * ## How does it work?
 * The hook returns the previously stored item, whose rendering is memoised, then waits for a few milliseconds,
 * and if nothing changes, switches to the new item which triggers the expensive, non memoised, rendering.
 * This behaviour only occurs when the new item has a different id from the stored item. Otherwise, it just returns the
 * new item, and the hook has no effect.
 * @param item
 */
const useDelayedRenderedItem = <TItem extends { id: number }>(item: TItem | undefined): TItem | undefined => {
  const [renderedItem, setRenderedItem] = React.useState<TItem | undefined>(item)

  React.useEffect(() => {
    if (item && renderedItem && renderedItem.id !== item.id) {
      const timeoutId = setTimeout(() => setRenderedItem(item), MILLISECS_BEFORE_SWITCH)
      return () => clearTimeout(timeoutId)
    }
  }, [renderedItem, item])

  if (item && renderedItem) {
    return item.id !== renderedItem.id ? renderedItem : item
  } else {
    return item
  }
}

export default useDelayedRenderedItem
